// components/RequireAuth.tsx - 認証保護コンポーネント

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { getPendingAccountUpdate } from '../utils/accountUpdateVerification';

interface RequireAuthProps {
  children: React.ReactElement; // children の型を限定
  requireSubscription?: boolean;
  disallowViewer?: boolean;
}

const RequireAuth = ({
  children,
  requireSubscription = true,
  disallowViewer = false,
}: RequireAuthProps): React.ReactElement => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // 認証状態確認中はローディング表示
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 未認証の場合はログインページへリダイレクト
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (disallowViewer && user?.isViewer) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Server-bound challenge is authoritative; local state can never bypass it.
  if (user?.mustVerifyCode) {
    const pendingAccountUpdate = getPendingAccountUpdate();
    if (pendingAccountUpdate?.userId === user.id && !user.verificationChallengeId) {
      const pendingEmail =
        pendingAccountUpdate.targetEmail || user?.pendingEmail || user?.email;
      const params = new URLSearchParams({
        mode: 'accountUpdate',
        accountUpdate: 'true',
      });
      if (pendingEmail) {
        params.set('email', pendingEmail);
      }
      return (
        <Navigate
          to={`/verify?${params.toString()}`}
          state={{ email: pendingEmail, mode: 'accountUpdate', accountUpdate: true }}
          replace
        />
      );
    }
    // 未認証時はNavigateのみで遷移する．再送APIはここでは呼ばない．
    return <Navigate to="/verify" state={{ email: user.email, mode: 'login', verificationChallengeId: user.verificationChallengeId }} replace />;
  }

  if (user?.mustChangePassword && location.pathname !== '/first-login-password') {
    return <Navigate to="/first-login-password" state={{ from: location }} replace />;
  }

  if (
    user?.hasActiveSubscription &&
    user?.needsProfileCompletion &&
    location.pathname !== '/trial-profile-setup'
  ) {
    return <Navigate to="/trial-profile-setup" state={{ from: location }} replace />;
  }

  // サブスクリプションが必要なページでサブスクリプションがない場合
  // ただしadminとtestユーザーは常にアクセス可能
  if (
    requireSubscription &&
    !user?.hasActiveSubscription &&
    !user?.isViewer &&
    !user?.isExpired &&
    !user?.isAdmin &&
    !user?.isVip &&
    !user?.isTrial
  ) {
    return <Navigate to="/subscription" state={{ from: location }} replace />;
  }

  // 認証済みの場合は子要素を表示
  return children;
};

export default RequireAuth;
